import { html, LitElement, nothing, unsafeCSS } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { TabNavigationType } from '../../../app/package/base/router/router-types/router-types';
import { TrackableEventAction } from '../../../app/package/base/service/tracking/tracking-service';
import { ProductSwitcherIconName, type ProductSwitcherItemProp } from '../../../app/package/base/types';
import { I18n } from '../../base/package/services/package/i18n/i18n.de';
import '../ui-input/ui-dropdown';
import styles from './ui-product-switcher-item.css?inline';
import '../ui-inlined-svg/ui-inlined-svg';

interface ProductSwitcherIcons {
  [key: string]: string;
}

const CName = 'ui-product-switcher-item';

@customElement(CName)
export class UIProductSwitcherItem extends LitElement {
  static readonly styles = unsafeCSS(styles);
  productSwitcherIcons: ProductSwitcherIcons = {
    tipicoSports: new URL('/assets/ui-core/icons/tipico-sports-icon.svg', import.meta.url).href,
    tipicoGames: window.$app.appSettings.isSHCasino
      ? new URL('/assets/ui-core/icons/slots-icon.svg', import.meta.url).href
      : new URL('/assets/ui-core/icons/tipico-games-icon.svg', import.meta.url).href,
  };

  @property({ type: String }) class = '';
  @property({ type: Object }) item: ProductSwitcherItemProp;
  @property({ type: Boolean }) isLoggedIn?: boolean;

  render() {
    const assetUrl = this.productSwitcherIcons[this.item.icon];
    const downloadAppIconUrl = new URL('/assets/ui-core/icons/download-app-icon.svg', import.meta.url).href;
    return html`
      <button
        class="product-switcher-item ${this.class}"
        @click=${(event: PointerEvent) => this._handleClick(event, this.item.link)}
        aria-label=${window.$app.$t.get(I18n.a11y.button.productSwitcher, { name: this.item.text })}
      >
        <div class="icon-wrapper">
          <ui-inlined-svg class="main-icon" .defer=${true} .src=${assetUrl}></ui-inlined-svg>
          ${
            this.item.showDownloadIcon
              ? html`
                <ui-inlined-svg
                  class="download-app-icon"
                  .defer=${true}
                  .src=${downloadAppIconUrl}
                ></ui-inlined-svg>
              `
              : nothing
          }
        </div>
        <p class="text">${this.item.text}</p>
      </button>
    `;
  }

  private _handleClick(event: PointerEvent, link: string) {
    window.$app.logger.log(`[${CName}] handleClick: dispatch 'navigate' event:`, link);
    event.preventDefault();
    this._handleTracking();
    window.$app.router.navigateTo(link, TabNavigationType.INTERNAL_SAME_EXTERNAL_NEW);
  }

  private _handleTracking(): void {
    let eventAction: TrackableEventAction | undefined;

    switch (this.item.icon) {
      case ProductSwitcherIconName.TIPICO_GAMES:
        eventAction = TrackableEventAction.GAMES_APP;
        break;
      case ProductSwitcherIconName.TIPICO_SPORTS:
        eventAction = this.item.showDownloadIcon ? TrackableEventAction.SPORTS_APP : TrackableEventAction.SPORTS;
        break;
    }

    if (eventAction) {
      window.$app.track.productSwitcher({
        event: window.$app.appSettings.isSHCasino ? 'xsellGamesSH' : 'productSwitcher',
        eventAction,
        status: this.isLoggedIn ? 'loggedIn' : 'loggedOut',
        action: 'goToGames',
      });
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    [CName]: UIProductSwitcherItem;
  }
}
